import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import { Survey } from "@/components/survey/Survey";
import QuestionSets from "@/components/survey/questionSets/questionSets";
import {
  GENDER_QUESTION_ID,
  SEX_QUESTION_ID,
} from "@/components/survey/questionTypes/dpp-triage";
import { useAppContext } from "@/contexts";
import {
  useAnswerSurveyQuestion,
  useGoto,
  usePartnerInfo,
  useTrackEvent,
} from "@/hooks";
import { getAutologinData } from "@/utils/cookies";
import { GenderProtoMap, SexProtoMap } from "@/utils/survey/retriage";

export const RetriageView: React.FC = () => {
  const { retriageEligibility, setEnrollmentType, surveyAnswers } =
    useAppContext();
  const { answerSurveyQuestion } = useAnswerSurveyQuestion();
  const goto = useGoto();
  const { employerSlug } = useParams<AppParams>();
  const { partnerInfo, hasPartnerError } = usePartnerInfo();
  const { trackAnonEvent, trackIdentifiedEvent } = useTrackEvent();

  const autologinData = useMemo(() => getAutologinData(), []);

  const onSurveyComplete = async () => {
    goto.openApp();
  };

  useEffect(() => {
    if (hasPartnerError) {
      goto.error();
    }
  }, [hasPartnerError, goto]);

  useEffect(() => {
    if (surveyAnswers.sex || surveyAnswers.gender) {
      return;
    }

    const { gender, sex } = retriageEligibility?.userProfile || {};

    if (!(gender && sex)) {
      return;
    }

    // Only pre-populate sex and gender if both values map cleanly to a respective question options.
    if (SexProtoMap[sex] && GenderProtoMap[gender]) {
      answerSurveyQuestion(
        SEX_QUESTION_ID,
        SexProtoMap[sex],
        {
          [GENDER_QUESTION_ID]: GenderProtoMap[gender],
        },
        true,
      );
    }
  }, [retriageEligibility, surveyAnswers]);

  useEffect(() => {
    setEnrollmentType("RETRIAGE");
  }, []);

  useEffect(() => {
    // We don't want to track retriage flow started events until after we know
    // the partner so that we can include the partner name in the event payload
    if (!partnerInfo) {
      return;
    }

    const retriageEntryPoint = autologinData ? "NOOM_APP" : "DIRECT_LINK";
    trackAnonEvent("b2bEnrollmentAnonRetriageFlowStarted", {
      retriageEntryPoint,
    });

    trackIdentifiedEvent("b2bEnrollmentIdentifiedRetriageFlowStarted", {
      retriageEntryPoint,
    });
  }, [autologinData, partnerInfo]);

  return (
    partnerInfo && (
      <Survey
        baseRoute={`/employer/${employerSlug}/rt`}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onSurveyComplete={onSurveyComplete}
        questionSet={QuestionSets.RETRIAGE_SURVEY}
      />
    )
  );
};
